import React, { useState, useEffect } from 'react';
import { Row, Col, Select, InputNumber, Input, Button, Tag, Space, Switch } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RulingConfigUtils, NUMERIC_OPERATORS, MATCH_OPERATORS, SCORECARD_TYPES } from '../../utils/rulingConfigUtils';

const { Option } = Select;

const ConditionBuilder = ({ 
  condition, 
  onChange, 
  onRemove, 
  showRemove = true,
  availableScorecards = ['scorecard_1', 'scorecard_2', 'scorecard_3', 'scorecard_4']
}) => {
  const [isGrouped, setIsGrouped] = useState(Boolean(condition.group));

  useEffect(() => {
    setIsGrouped(Boolean(condition.group));
  }, [condition]);

  const handleScorecardChange = (scorecard) => {
    const scorecardNum = parseInt(scorecard.split('_')[1]);
    const type = RulingConfigUtils.getScorecardType(scorecardNum);
    const operators = RulingConfigUtils.getAvailableOperators(scorecardNum);
    const defaultOperator = Object.values(operators)[0];

    const newCondition = {
      ...condition,
      scorecard,
      type,
      operator: defaultOperator,
      value: type === SCORECARD_TYPES.NUMERIC ? 0 : "",
    };

    // Clear min/max if switching from between operator
    if (condition.operator === NUMERIC_OPERATORS.BETWEEN || condition.operator === NUMERIC_OPERATORS.NOT_BETWEEN) {
      delete newCondition.min;
      delete newCondition.max;
    }

    onChange(newCondition);
  };

  const handleOperatorChange = (operator) => {
    const newCondition = { ...condition, operator };

    // Handle between operators
    if (operator === NUMERIC_OPERATORS.BETWEEN || operator === NUMERIC_OPERATORS.NOT_BETWEEN) {
      newCondition.min = 0;
      newCondition.max = 10;
      delete newCondition.value;
    } else if (operator === MATCH_OPERATORS.IN || operator === MATCH_OPERATORS.NOT_IN) {
      newCondition.value = [];
    } else {
      // Clear min/max for other operators
      delete newCondition.min;
      delete newCondition.max;
      if (!newCondition.value) {
        newCondition.value = condition.type === SCORECARD_TYPES.NUMERIC ? 0 : "";
      }
    }

    onChange(newCondition);
  };

  const handleValueChange = (value) => {
    onChange({ ...condition, value });
  };

  const handleMinMaxChange = (field, value) => {
    onChange({ ...condition, [field]: value });
  };

  const handleArrayValueAdd = (newValue) => {
    if (newValue && newValue.trim() && !condition.value.includes(newValue.trim())) {
      const updatedArray = [...(condition.value || []), newValue.trim()];
      onChange({ ...condition, value: updatedArray });
    }
  };

  const handleArrayValueRemove = (valueToRemove) => {
    const updatedArray = (condition.value || []).filter(v => v !== valueToRemove);
    onChange({ ...condition, value: updatedArray });
  };

  const toggleGrouping = (checked) => {
    if (checked) {
      // Convert to group
      const newCondition = {
        group: [{ ...condition }],
        group_logic: 'AND'
      };
      delete newCondition.scorecard;
      delete newCondition.operator;
      delete newCondition.value;
      delete newCondition.type;
      onChange(newCondition);
    } else {
      // Convert from group to single condition
      if (condition.group && condition.group.length > 0) {
        onChange(condition.group[0]);
      } else {
        onChange(RulingConfigUtils.createConditionTemplate());
      }
    }
    setIsGrouped(checked);
  };

  const addGroupCondition = () => {
    const newGroupCondition = RulingConfigUtils.createConditionTemplate();
    onChange({
      ...condition,
      group: [...(condition.group || []), newGroupCondition]
    });
  };

  const updateGroupCondition = (index, newCondition) => {
    const updatedGroup = [...(condition.group || [])];
    updatedGroup[index] = newCondition;
    onChange({
      ...condition,
      group: updatedGroup
    });
  };

  const removeGroupCondition = (index) => {
    const updatedGroup = (condition.group || []).filter((_, i) => i !== index);
    if (updatedGroup.length === 0) {
      // If no conditions left, convert back to single condition
      onChange(RulingConfigUtils.createConditionTemplate());
      setIsGrouped(false);
    } else {
      onChange({
        ...condition,
        group: updatedGroup
      });
    }
  };

  const renderValueInput = () => {
    if (!condition.scorecard) return null;

    const isNumeric = condition.type === SCORECARD_TYPES.NUMERIC;
    const isBetween = condition.operator === NUMERIC_OPERATORS.BETWEEN || condition.operator === NUMERIC_OPERATORS.NOT_BETWEEN;
    const isArrayOperator = condition.operator === MATCH_OPERATORS.IN || condition.operator === MATCH_OPERATORS.NOT_IN;

    if (isBetween) {
      return (
        <Row gutter={8}>
          <Col span={12}>
            <InputNumber
              placeholder="Min"
              value={condition.min}
              onChange={(value) => handleMinMaxChange('min', value)}
              style={{ width: '100%' }}
              size="small"
            />
          </Col>
          <Col span={12}>
            <InputNumber
              placeholder="Max"
              value={condition.max}
              onChange={(value) => handleMinMaxChange('max', value)}
              style={{ width: '100%' }}
              size="small"
            />
          </Col>
        </Row>
      );
    }

    if (isArrayOperator) {
      return (
        <ArrayValueInput
          values={condition.value || []}
          onAdd={handleArrayValueAdd}
          onRemove={handleArrayValueRemove}
        />
      );
    }

    if (isNumeric) {
      return (
        <InputNumber
          value={condition.value}
          onChange={handleValueChange}
          style={{ width: '100%' }}
          size="small"
          step={0.1}
        />
      );
    }

    return (
      <Input
        value={condition.value}
        onChange={(e) => handleValueChange(e.target.value)}
        placeholder="Enter value"
        size="small"
      />
    );
  };

  if (isGrouped && condition.group) {
    return (
      <div style={{ 
        border: '1px dashed var(--border-secondary)', 
        borderRadius: 4, 
        padding: 12,
        backgroundColor: 'var(--bg-tertiary)'
      }}>
        <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
          <Col>
            <Switch 
              size="small"
              checked={isGrouped}
              onChange={toggleGrouping}
            />
          </Col>
          <Col>
            <span style={{ fontSize: '12px', fontWeight: 500 }}>Group Logic:</span>
          </Col>
          <Col>
            <Select
              value={condition.group_logic}
              onChange={(logic) => onChange({ ...condition, group_logic: logic })}
              size="small"
              style={{ width: 80 }}
            >
              <Option value="AND">AND</Option>
              <Option value="OR">OR</Option>
            </Select>
          </Col>
          {showRemove && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={onRemove}
                danger
              />
            </Col>
          )}
        </Row>

        <div style={{ marginLeft: 16 }}>
          {condition.group.map((groupCondition, index) => (
            <div key={index} style={{ marginBottom: index < condition.group.length - 1 ? 8 : 0 }}>
              <ConditionBuilder
                condition={groupCondition}
                onChange={(newCondition) => updateGroupCondition(index, newCondition)}
                onRemove={() => removeGroupCondition(index)}
                showRemove={condition.group.length > 1}
                availableScorecards={availableScorecards}
              />
            </div>
          ))}
        </div>

        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={addGroupCondition}
          style={{ marginTop: 8, marginLeft: 16 }}
        >
          Add Condition
        </Button>
      </div>
    );
  }

  const scorecardNum = condition.scorecard ? parseInt(condition.scorecard.split('_')[1]) : 1;
  const availableOperators = RulingConfigUtils.getAvailableOperators(scorecardNum);

  return (
    <Row gutter={8} align="middle">
      <Col span={1}>
        <Switch 
          size="small"
          checked={isGrouped}
          onChange={toggleGrouping}
        />
      </Col>
      <Col span={5}>
        <Select
          value={condition.scorecard}
          onChange={handleScorecardChange}
          size="small"
          style={{ width: '100%' }}
          placeholder="Scorecard"
        >
          {availableScorecards.map(scorecard => (
            <Option key={scorecard} value={scorecard}>
              {scorecard.replace('_', ' ')}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={5}>
        <Select
          value={condition.operator}
          onChange={handleOperatorChange}
          size="small"
          style={{ width: '100%' }}
          placeholder="Operator"
        >
          {Object.entries(availableOperators).map(([key, operator]) => (
            <Option key={operator} value={operator}>
              {RulingConfigUtils.getOperatorLabel(operator)}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={6}>
        {renderValueInput()}
      </Col>
      <Col span={2}>
        <Tag size="small" color={condition.type === SCORECARD_TYPES.NUMERIC ? 'blue' : 'purple'}>
          {condition.type}
        </Tag>
      </Col>
      {showRemove && (
        <Col span={2}>
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={onRemove}
            danger
          />
        </Col>
      )}
    </Row>
  );
};

// Component for handling array values (for IN/NOT_IN operators)
const ArrayValueInput = ({ values = [], onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('');
  const [inputVisible, setInputVisible] = useState(false);

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue);
      setInputValue('');
      setInputVisible(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {values.map((value, index) => (
        <Tag
          key={index}
          closable
          onClose={() => onRemove(value)}
          style={{ marginBottom: 4 }}
        >
          {value}
        </Tag>
      ))}
      
      {inputVisible ? (
        <Input
          size="small"
          style={{ width: 100 }}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleAdd}
          onBlur={handleAdd}
          autoFocus
        />
      ) : (
        <Tag
          onClick={() => setInputVisible(true)}
          style={{
            background: 'var(--bg-tertiary)',
            borderStyle: 'dashed',
            cursor: 'pointer'
          }}
        >
          <PlusOutlined /> Add Value
        </Tag>
      )}
    </div>
  );
};

export default ConditionBuilder;